import { type Attributes, type Span, SpanStatusCode, trace } from "@opentelemetry/api";
import { flatten } from "flat";

import { createLogger } from "@/logger";
import type { BootKind } from "@/store/bootRun";

const log = createLogger("telemetry");

import { TELEMETRY_CATEGORY_ATTRIBUTE, type TelemetryCategory } from "./categories";
import { resetSessionAttributesForTests, setSessionAttribute } from "./sessionAttributes";

export { TELEMETRY_CATEGORY_ATTRIBUTE, type TelemetryCategory } from "./categories";
export { ACTION_DURATION_CEILING_MS, clampActionDuration } from "./durationClamp";
export { DEFAULT_SAMPLE_RATES, SessionCategorySampler, type TelemetrySampleRates } from "./sampler";
export { SessionAttributesSpanProcessor, setSessionAttribute, setSessionAttributes } from "./sessionAttributes";
export { initializeTabVisibility, resetTabVisibilityForTests, wasTabHiddenDuringSession } from "./tabVisibility";

const TRACER_NAME = "storyboard-web";

export type TelemetryAttrs = Record<string, unknown>;

/**
 * The three screens that show the user "Something went wrong". Used as both the `error.kind`
 * attribute on `error_screen.rendered` and the Sentry `errorScreen` tag, so the two never drift.
 */
export type ErrorScreenKind = "component" | "route" | "storage";

type PendingEvent = {
  name: string;
  attributes?: TelemetryAttrs;
  category: TelemetryCategory;
  startTime: Date;
  endTime: Date;
  isError?: boolean;
};

/**
 * Events recorded before the exporter starts (storage init, readiness probe, boot failures run
 * before `initializeHoneycomb`) are buffered and replayed with their original timestamps once the
 * SDK is up. If telemetry never starts (kill switch), the buffer is dropped with the page.
 */
const PENDING_EVENT_LIMIT = 100;
let pendingEvents: PendingEvent[] = [];
let started = false;
let flushHandler: (() => Promise<void>) | undefined;
let visibilityFlushListener: (() => void) | undefined;

/** Registered by the exporter init so this module can flush without importing vendor code. */
export function setTelemetryFlushHandler(handler: () => Promise<void>): void {
  flushHandler = handler;
}

/** Called by the exporter init once the SDK has started; replays any buffered events. */
export function markTelemetryStarted(): void {
  started = true;
  const buffered = pendingEvents;
  pendingEvents = [];
  for (const event of buffered) {
    emitEvent(event);
  }
  // Web Vitals and other lifecycle-end events fire as the page hides; flush so they survive. The
  // flush is deferred one task so listeners registered after this one (web-vitals finalizes its
  // metrics on the same visibilitychange dispatch) get to record before the buffer ships.
  if (typeof document !== "undefined" && !visibilityFlushListener) {
    visibilityFlushListener = () => {
      if (document.visibilityState === "hidden") {
        setTimeout(() => void flushTelemetry(), 0);
      }
    };
    document.addEventListener("visibilitychange", visibilityFlushListener);
  }
}

/** Forces the exporter to ship buffered spans now. No-op until the exporter has started. */
export async function flushTelemetry(): Promise<void> {
  try {
    await flushHandler?.();
  } catch (error) {
    log.warn("[flushTelemetry] Flush failed", { error });
  }
}

/** Test-only reset of module state. */
export function resetTelemetryForTests(): void {
  started = false;
  flushHandler = undefined;
  pendingEvents = [];
  bootPhase = undefined;
  bootedThisPageLoad = false;
  resetSessionAttributesForTests();
  if (typeof document !== "undefined" && visibilityFlushListener) {
    document.removeEventListener("visibilitychange", visibilityFlushListener);
  }
  visibilityFlushListener = undefined;
}

function emitEvent({ name, attributes, category, startTime, endTime, isError }: PendingEvent): void {
  const span = trace.getTracer(TRACER_NAME).startSpan(name, {
    startTime,
    attributes: {
      ...(attributes ? (flatten(attributes) as Attributes) : {}),
      [TELEMETRY_CATEGORY_ATTRIBUTE]: category,
    },
  });
  span.setStatus({ code: isError ? SpanStatusCode.ERROR : SpanStatusCode.OK });
  span.end(endTime);
}

/**
 * Records a point-in-time telemetry event (a zero-duration span). Defaults to the `reliability`
 * category, which ships unsampled — rare, high-value events (storage recovery, AppSync failures,
 * error screens) should use it; anything high-volume must pass a sampled category.
 */
export function recordEvent(
  name: string,
  attributes?: TelemetryAttrs,
  category: TelemetryCategory = "reliability",
  options?: { isError?: boolean },
): void {
  try {
    log.debug(`[recordEvent] Telemetry event`, { name, attributes });
    const now = new Date();
    const event: PendingEvent = { name, attributes, category, startTime: now, endTime: now, isError: options?.isError };
    if (!started) {
      if (pendingEvents.length < PENDING_EVENT_LIMIT) pendingEvents.push(event);
      return;
    }
    emitEvent(event);
  } catch (error) {
    log.error(`[recordEvent] Error recording event`, { name, attributes, error });
  }
}

/** {@link recordEvent} + immediate flush; use right before a reload/teardown destroys the page. */
export async function recordEventAndFlush(
  name: string,
  attributes?: TelemetryAttrs,
  category: TelemetryCategory = "reliability",
): Promise<void> {
  recordEvent(name, attributes, category);
  await flushTelemetry();
}

/**
 * Records a completed duration measurement as a span from `startTime` to now. Used for perceived
 * measurements whose start predates the decision to report (boot-to-interactive, list loads).
 */
export function recordSpan(
  name: string,
  startTime: Date,
  attributes?: TelemetryAttrs,
  category: TelemetryCategory = "perceived",
  options?: { isError?: boolean },
): void {
  try {
    const event: PendingEvent = {
      name,
      attributes,
      category,
      startTime,
      endTime: new Date(),
      isError: options?.isError,
    };
    if (!started) {
      if (pendingEvents.length < PENDING_EVENT_LIMIT) pendingEvents.push(event);
      return;
    }
    emitEvent(event);
  } catch (error) {
    log.error(`[recordSpan] Error recording span`, { name, attributes, error });
  }
}

/** Starts a live span with the sampling category stamped at creation. Caller ends it. */
export function startTelemetrySpan(
  name: string,
  { category, attributes, startTime }: { category: TelemetryCategory; attributes?: Attributes; startTime?: Date },
): Span {
  return trace.getTracer(TRACER_NAME).startSpan(name, {
    startTime,
    attributes: { ...attributes, [TELEMETRY_CATEGORY_ATTRIBUTE]: category },
  });
}

/**
 * The kind of boot for the current page load, threaded onto API spans and boot measurements as `boot.phase`. The
 * type lives with the boot run; the attribute keeps its name, which Honeycomb queries and the boot SLO read.
 */
let bootPhase: BootKind | undefined;
let bootedThisPageLoad = false;

/** Called by the bootstrap orchestrator once it has decided full vs incremental. */
export function beginBootPhase(mode: "full" | "incremental"): BootKind {
  bootPhase = mode === "full" ? "cold" : bootedThisPageLoad ? "incremental" : "warm";
  bootedThisPageLoad = true;
  // Stamp the phase as a session attribute so it lands on every subsequent span — most importantly
  // `boot.interactive`, which is recorded from a gate subscriber that can't see this module's state.
  // Without this the metric reported `boot.phase: unknown` on nearly every boot.
  setSessionAttribute("boot.phase", bootPhase);
  return bootPhase;
}

export function currentBootPhase(): BootKind | undefined {
  return bootPhase;
}
